import { registerLocaleData } from '@angular/common';
import localeGermanSwiss from '@angular/common/locales/de-CH';
import localeFrenchSwiss from '@angular/common/locales/fr-CH';
import localeItalianSwiss from '@angular/common/locales/it-CH';
import { inject, Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { changei18n } from '@infragistics/igniteui-angular';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { GridResources } from '../core/utils/grid-resources/grid-resources';
import { BasicDataModel } from '../models/basic-data.model';
import { TranslationLanguageModel } from '../models/translation-language.model';
import { BasicDataService } from './basic-data.service';
import { RouteService } from './route.service';
import { SettingsStorage } from './storage/settings.storage';

@Injectable({
    providedIn: 'root'
})
export class TranslationService {
    readonly onLanguageChanged = new Subject<void>();
    readonly allowedLanguagesLoadedSub = new Subject<void>();
    readonly setupCompletedSub = new Subject<void>();

    get allowedLanguages(): TranslationLanguageModel[] {
        return this.userAllowedLanguages;
    }

    private availableLanguages: TranslationLanguageModel[] = [
        { code: 'de', name: 'Deutsch', gridResources: GridResources.German },
        { code: 'en', name: 'English', gridResources: GridResources.English },
        { code: 'it', name: 'Italiano', gridResources: GridResources.Italian },
        { code: 'fr', name: 'Français', gridResources: GridResources.French }
    ];
    private userAllowedLanguages: TranslationLanguageModel[] = [];
    private currentLanguage: TranslationLanguageModel;
    private isLoggedIn = false;
    private areAllowedLangsLoaded = false;
    private isLoadingAllowedLangs = false;
    private readonly translateService = inject(TranslateService);
    private readonly settingsStorage = inject(SettingsStorage);
    private readonly basicDataService = inject(BasicDataService);
    private readonly dateAdapter = inject(DateAdapter<Date>);
    private readonly routeService = inject(RouteService);

    private get shouldLoadAllowedLanguages(): boolean {
        return this.isLoggedIn && !this.areAllowedLangsLoaded;
    }

    private get currentAllowedLangSet(): TranslationLanguageModel[] {
        return this.allowedLanguages.length > 0 ? this.allowedLanguages : this.availableLanguages;
    }

    constructor() {
        const currentUserLanguage = this.settingsStorage.getCurrentLanguage();
        this.currentLanguage = this.currentAllowedLangSet.find(l => l.code === currentUserLanguage) ?? this.getBrowserOrDefaultLanguage();
    }

    setup(isLoggedIn: boolean): Observable<void> {
        registerLocaleData(localeGermanSwiss, 'de');
        registerLocaleData(localeFrenchSwiss, 'fr');
        registerLocaleData(localeItalianSwiss, 'it');
        this.translateService.onLangChange.subscribe(() => this.onLanguageChanged.next());

        this.isLoggedIn = isLoggedIn;
        const observable = isLoggedIn ? this.loadAllowedLanguages() : of();

        return observable.pipe(
            finalize(() => this.completeSetup()),
            map(() => {})
        );
    }

    /**
     * Retrieves all available languages.
     */
    getAvailableLanguages(): TranslationLanguageModel[] {
        return this.availableLanguages;
    }

    /**
     * Retrieves current selected language.
     */
    getCurrent(): TranslationLanguageModel {
        return this.currentLanguage;
    }

    /**
     * Retrieves a translated text based on the provided key.
     * @param key Translation text's key.
     * @param params Translation parameters
     */
    getText(key: string, params?: any): Observable<string> {
        return this.translateService.get(key, params);
    }

    /**
     * Defines a new default language.
     * @param code New language code.
     */
    switch(code: string): void {
        if (this.shouldLoadAllowedLanguages && !this.isLoadingAllowedLangs) {
            this.loadAllowedLanguages().subscribe(() => this.makeLangSwitch(code));
        } else if (this.shouldLoadAllowedLanguages) {
            this.allowedLanguagesLoadedSub.subscribe(() => this.makeLangSwitch(code));
        } else {
            this.makeLangSwitch(code);
        }
    }

    private isLanguageAllowed(code: string): boolean {
        return !!this.currentAllowedLangSet.find(al => al.code === code);
    }

    private completeSetup(): void {
        const url = new URL(location.href);
        const langParam = url.searchParams.get('lang');
        url.searchParams.delete('lang');
        if (!!langParam) {
            window.history.replaceState({}, '', url.toString());
            const language = this.currentAllowedLangSet.find(al => al.code === langParam);
            if (!!language) {
                this.currentLanguage = language;
            }
        }

        if (!this.isLanguageAllowed(this.currentLanguage.code)) {
            this.currentLanguage = this.getBrowserOrDefaultLanguage();
            this.settingsStorage.saveCurrentLanguage(this.currentLanguage.code);
            this.routeService.reloadPage(true);
            return;
        }

        this.translateService.addLangs(this.currentAllowedLangSet.map(l => l.code));
        this.translateService.setFallbackLang(this.currentLanguage.code);
        this.useNewLanguage(this.currentLanguage.code);

        this.setupCompletedSub.next();
    }

    private makeLangSwitch(code: string): void {
        if (this.currentLanguage?.code === code) {
            return;
        }

        const newLangModel = this.currentAllowedLangSet.find(l => l.code === code);
        if (!newLangModel) {
            return;
        }

        this.useNewLanguage(code);
        this.settingsStorage.saveCurrentLanguage(code);

        this.routeService.reloadPage(true);
    }

    /**
     * Retrieves all languages that can be used (by config).
     */
    private loadAllowedLanguages(): Observable<TranslationLanguageModel[]> {
        this.areAllowedLangsLoaded = false;
        this.isLoadingAllowedLangs = true;

        return this.basicDataService.get().pipe(
            map((data: BasicDataModel) => {
                const newLanguages: TranslationLanguageModel[] = [];

                if (data.languageDe) {
                    newLanguages.push(this.availableLanguages.find(al => al.code === 'de')!);
                }

                if (data.languageEn) {
                    newLanguages.push(this.availableLanguages.find(al => al.code === 'en')!);
                }

                if (data.languageFr) {
                    newLanguages.push(this.availableLanguages.find(al => al.code === 'fr')!);
                }

                if (data.languageIt) {
                    newLanguages.push(this.availableLanguages.find(al => al.code === 'it')!);
                }

                this.userAllowedLanguages = newLanguages;
                this.areAllowedLangsLoaded = true;
                this.isLoadingAllowedLangs = false;

                this.allowedLanguagesLoadedSub.next();

                return newLanguages;
            })
        );
    }

    private useNewLanguage(code: string): void {
        const selectedLang = this.currentAllowedLangSet.find(l => l.code === code);

        if (!selectedLang) {
            return;
        }

        const resources = selectedLang.gridResources;

        changei18n(resources);
        this.translateService.use(code);
        this.dateAdapter.setLocale(code);
        this.settingsStorage.saveCurrentLanguage(code);
    }

    private getBrowserOrDefaultLanguage(): TranslationLanguageModel {
        const lang = this.translateService.getBrowserLang()?.split('-')?.at(0);
        return this.currentAllowedLangSet.find(l => l.code === lang) ?? this.currentAllowedLangSet[0];
    }
}
